/**
 * Draws a resolved layout: a split is a flex box, a slot mounts its module inside an error boundary.
 * Each module carries a view-transition-name, so when the layout changes the browser morphs every
 * module from its old box to its new one.
 */
import type { CSSProperties } from 'react';
import { useApp } from '../react/app';
import { useAppearance } from '../react/hooks';
import { pickModuleVariant } from '../modules/contract';
import { flexOf } from '../layout/geometry';
import type { LayoutNode } from '../layout/model';
import { Problem } from '../ui-kit/Panel';
import { ErrorBoundary } from './ErrorBoundary';
import css from './Shell.module.css';

export function LayoutView({ node }: { node: LayoutNode }) {
  if (node.kind === 'slot') return <Slot module={node.module} style={flexOf(node.size)} />;
  return (
    <div
      className={css.split}
      data-split={node.id}
      style={{ ...flexOf(node.size), flexDirection: node.direction }}
    >
      {node.children.map((child) => (
        <LayoutView key={child.kind === 'slot' ? child.module : child.id} node={child} />
      ))}
    </div>
  );
}

function Slot({ module, style }: { module: string; style: CSSProperties }) {
  const { modules } = useApp();
  const { moduleVariants } = useAppearance();
  const def = modules.get(module);
  const variant = def ? pickModuleVariant(def, moduleVariants[module]) : null;
  return (
    <div
      className={css.slot}
      data-module={module}
      data-variant={variant?.id}
      // the screen margin (Settings) insets the modules; the edge-to-edge bars apply it inside, to their content
      style={{ ...style, viewTransitionName: `module-${module}`, paddingInline: def?.fullBleed ? 0 : 'var(--frame-padding, 0px)' }}
    >
      {variant ? (
        <ErrorBoundary name={`module ${module}`} resetKey={variant.id}>
          <variant.Component />
        </ErrorBoundary>
      ) : (
        <Problem title={`module "${module}" is not registered`} detail="Add it to the module registry or remove it from the preset." />
      )}
    </div>
  );
}
